<script setup lang="ts">
import logo from './images/logo.svg';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    placement?: 'main' | 'inner';
  }>(),
  {
    placement: 'main',
  },
);

const UTM_QUERY = {
  main: 'utm_source=main',
  inner: 'utm_source=inner',
} as const;

const PROJECT_URL = 'https://35.cre.ru/';
const VIDEO_URL =
  'https://s3.regru.cloud/cre-prod/media/video/02_speakers_clips_720.mp4';

const projectUrl = computed(
  () => `${PROJECT_URL}?${UTM_QUERY[props.placement]}`,
);
</script>

<template>
  <section v-bind="$attrs" :class="$style.teaser">
    <span :class="$style.teaser__tint" aria-hidden="true" />

    <a
      :href="projectUrl"
      target="_blank"
      rel="noopener noreferrer"
      :class="$style.teaser__overlay"
      aria-label="35 лет рынку недвижимости: тизер фильма"
    />

    <header :class="$style.teaser__header">
      <img
        :src="logo"
        alt="35 лет рынку недвижимости"
        :class="$style.teaser__logo"
      />
    </header>

    <div :class="$style.teaser__card">
      <p :class="$style.teaser__title">
        <span :class="$style.teaser__titleLine">История рынка,</span>
        {{ ' ' }}
        <span :class="$style.teaser__titleLine">рассказанная его героями</span>
      </p>

      <p :class="$style.teaser__description">
        CRE представляет тизер фильма «35 лет рынку недвижимости». От стихийной
        торговли 1990-х и первых иностранных инвестиций до деловых кварталов и
        современных логистических комплексов — история отрасли в архивных кадрах
        и воспоминаниях ее участников.
      </p>

      <div :class="$style.teaser__media">
        <video
          :src="VIDEO_URL"
          :class="$style.teaser__video"
          autoplay
          muted
          loop
          playsinline
          disablepictureinpicture
          aria-hidden="true"
        />
      </div>

      <div :class="$style.teaser__footer">
        <a
          :href="projectUrl"
          target="_blank"
          rel="noopener noreferrer"
          :class="$style.teaser__button"
        >
          Стать героем фильма
        </a>
      </div>
    </div>
  </section>
</template>

<style module lang="scss">
.teaser {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background: var(--color-black-1);

  &,
  * {
    box-sizing: border-box;
  }

  /* Фон по слоям макета: картинка с бликом, размытое затемнение, тонировка.
     Отрицательные z-index держат слои под контентом внутри isolate */
  &::before,
  &::after,
  &__tint {
    position: absolute;
    pointer-events: none;
  }

  &::before,
  &::after {
    content: '';
  }

  &::before {
    inset: 0;
    z-index: -3;
    background-position: center;
    background-size: cover;
    background-image: image-set(
      url('./images/background.jpg') 1x,
      url('./images/background@2x.jpg') 2x
    );
  }

  &::after {
    inset: -103px -20px -60px -56px;
    z-index: -2;
    background: #000;
    filter: blur(120px);
  }

  &__tint {
    inset: 0;
    z-index: -1;
    background: linear-gradient(
      to bottom,
      rgb(64 71 94 / 83%),
      rgb(64 71 94 / 55%)
    );
    mix-blend-mode: color;
  }

  /* Ссылка поверх всего блока; кнопка ниже поднимается над ней через z-index.
     У карточки z-index нет, чтобы не создавать stacking context */
  &__overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  &__header {
    display: flex;
    align-items: center;
    height: 82px;
    padding: 0 var(--space-2-5x);
  }

  &__logo {
    display: block;
    width: 100%;
    max-width: 395px;
    height: auto;
  }

  &__card {
    display: grid;
    grid-template:
      'title media' auto
      'text media' 1fr
      'footer media' auto
      / minmax(0, 484px) minmax(0, 484px);
    justify-content: space-between;
    column-gap: var(--space-2-5x);
    padding: var(--space-2-5x);
    border: 1px solid var(--color-black-2);
    border-bottom: 0;
  }

  &__title,
  &__description {
    margin: 0;
    color: var(--color-white);
    word-break: break-word;
  }

  &__title {
    grid-area: title;
    margin-bottom: var(--space-2x);
    @include text_h3;
  }

  &__titleLine {
    display: block;
  }

  &__description {
    grid-area: text;
    font-size: 16px;
    line-height: 1.25;
  }

  &__media {
    grid-area: media;
    aspect-ratio: 16 / 9;
    background: var(--color-black-1);
  }

  &__video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__footer {
    display: flex;
    grid-area: footer;
    padding-top: var(--space-2-5x);
  }

  &__button {
    position: relative;
    z-index: 2;
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 var(--space-2-5x);
    background: var(--color-red-1);
    color: var(--color-white);
    text-decoration: none;
    white-space: nowrap;
    @include text_button3;
  }
}

@media (hover: hover) {
  .teaser__button {
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

/* Перенос в заголовке только под десктопную колонку, на остальных ширинах он оставляет пустоту справа */
@media (max-width: 1379px) {
  .teaser {
    &__card {
      grid-template:
        'title title' auto
        'text media' 1fr
        'footer media' auto
        / minmax(0, 1fr) minmax(0, 1fr);
      gap: var(--space-2-5x);
    }

    &__title {
      margin-bottom: 0;
    }

    &__titleLine {
      display: inline;
    }

    &__footer {
      padding-top: 0;
    }
  }
}

@media (min-width: 600px) and (max-width: 1379px) {
  .teaser__title {
    @include text_h4;
    font-size: 30px;
  }
}

@media (min-width: 600px) and (max-width: 1023px) {
  .teaser__description {
    font-size: 14px;
  }
}

@media (max-width: 767px) {
  .teaser {
    &__header {
      height: auto;
      padding: var(--space-2x);
    }

    &__card {
      gap: var(--space-2x);
      padding: var(--space-2x);
      border-right: 0;
      border-left: 0;
    }
  }
}

@media (max-width: 599px) {
  .teaser {
    &__card {
      grid-template:
        'title'
        'text'
        'media'
        'footer'
        / minmax(0, 1fr);
    }

    &__title {
      @include text_h3_mobile;
    }

    &__description {
      font-size: 14px;
    }
  }
}

@media (max-width: 479px) {
  .teaser__button {
    flex-grow: 1;
  }
}

@media (max-width: 360px) {
  .teaser__button {
    padding: 0 var(--space-1x);
  }
}
</style>
